import { useContext, useEffect, useRef } from "react";
import { AuthContext } from "react-oidc-context";

import {
  BOOT_PREFETCH_MAX_AGE_SECONDS,
  isBootPrefetchEnabled,
  markBootPrefetched,
  readBootHint,
} from "@/lib/bootHint";
import { buildLedgerEntriesArgs } from "@/providers/LedgerEntryListProvider/LedgerEntryListProvider";
import { useAppDispatch } from "@/redux/hooks";
import { enhancedApi } from "@/redux/services/enhancedApi";
import type { AppDispatch } from "@/redux/store";
import { useGetSelfQuery } from "@generated/redux/services/generated/api";

/**
 * Starts the dashboard's two account-scoped reads for `customerAccountUuid`:
 * balances, with the args `useBalances` passes, and the first ledger page,
 * with the args the dashboard's `LedgerEntryListProvider` builds (no product,
 * no filters, no cursor). Matching args are what let those hooks find the
 * prefetched entries instead of requesting again.
 */
export const prefetchBootAccountData = (
  dispatch: AppDispatch,
  customerAccountUuid: string
) => {
  const options = { ifOlderThan: BOOT_PREFETCH_MAX_AGE_SECONDS };
  dispatch(
    enhancedApi.util.prefetch(
      "getCustomerAccountBalances",
      { customerAccountId: customerAccountUuid },
      options
    )
  );
  dispatch(
    enhancedApi.util.prefetch(
      "getLedgerEntries",
      buildLedgerEntriesArgs({
        customerAccountId: customerAccountUuid,
        filters: {},
        cursor: undefined,
      }),
      options
    )
  );
};

/**
 * Speculative boot prefetch (`lib/bootHint.ts`). While `/self` is still in
 * flight, start balances and ledger page 1 for the account this same user
 * last viewed, so the dashboard does not wait on `/self` first. Only with a
 * live token: an expired one would cost a 401 and a renew round trip ahead of
 * `/self`. Once per mount, and never after `/self` has resolved (a remount
 * over a cached `/self` is not a boot).
 *
 * It must run in a component that is mounted WHILE `/self` is pending —
 * `OnboardingRouter`, before its loading early return. `CustomerAccountProvider`
 * only mounts after `/self` resolves, so there it could never fire. The hint
 * itself is written by `CustomerAccountProvider`, once the account resolves.
 */
export function useBootPrefetch(): void {
  const { isLoading } = useGetSelfQuery();
  const dispatch = useAppDispatch();

  // Read the context directly rather than `useAuth()`, which warns when there
  // is no AuthProvider (Storybook, unit tests): no auth means no prefetch.
  const oidcUser = useContext(AuthContext)?.user;
  const oidcSub = oidcUser?.profile?.sub;

  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current) return;
    if (!isLoading || !isBootPrefetchEnabled()) {
      doneRef.current = true;
      return;
    }
    if (!oidcUser || oidcUser.expired || !oidcSub) return;
    doneRef.current = true;
    const hintedAccountUuid = readBootHint(oidcSub);
    if (!hintedAccountUuid) return;
    prefetchBootAccountData(dispatch, hintedAccountUuid);
    markBootPrefetched();
  }, [isLoading, oidcUser, oidcSub, dispatch]);
}
